import { useId, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { AuthLayout } from './AuthLayout';
import { useUpdatePassword } from './hooks/useAuth';

const schema = z
  .object({
    yeniSifre: z
      .string()
      .min(8, 'Şifre en az 8 karakter olmalı')
      .regex(/[A-ZÇĞİÖŞÜ]/, 'En az 1 büyük harf')
      .regex(/[a-zçğıöşü]/, 'En az 1 küçük harf')
      .regex(/[0-9]/, 'En az 1 rakam'),
    sifreTekrar: z.string(),
  })
  .refine((data) => data.yeniSifre === data.sifreTekrar, {
    message: 'Şifreler eşleşmiyor',
    path: ['sifreTekrar'],
  });
type FormData = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const nav = useNavigate();
  const update = useUpdatePassword();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const yeniSifreId = useId();
  const sifreTekrarId = useId();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (data) => {
    setError(null);
    try {
      await update.mutateAsync(data.yeniSifre);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bir hata oluştu');
    }
  });

  if (success) {
    return (
      <AuthLayout title={t('reset.basarili')}>
        <p className="text-sm text-muted-foreground mb-6">
          Şimdi yeni şifrenle dashboard'a yönlendirileceksin.
        </p>
        <Button onClick={() => nav('/dashboard')} className="w-full">
          {t('reset.girise_git')}
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('reset.baslik')} subtitle={t('reset.alt_baslik')}>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div>
          <label htmlFor={yeniSifreId} className="block text-sm font-medium mb-1.5">
            {t('reset.yeni_sifre')}
          </label>
          <Input
            id={yeniSifreId}
            type="password"
            autoComplete="new-password"
            {...register('yeniSifre')}
            aria-invalid={!!errors.yeniSifre}
          />
          {errors.yeniSifre && (
            <p className="mt-1 text-xs text-danger">{errors.yeniSifre.message}</p>
          )}
        </div>

        <div>
          <label htmlFor={sifreTekrarId} className="block text-sm font-medium mb-1.5">
            {t('reset.sifre_tekrar')}
          </label>
          <Input
            id={sifreTekrarId}
            type="password"
            autoComplete="new-password"
            {...register('sifreTekrar')}
            aria-invalid={!!errors.sifreTekrar}
          />
          {errors.sifreTekrar && (
            <p className="mt-1 text-xs text-danger">{errors.sifreTekrar.message}</p>
          )}
        </div>

        {error && (
          <div className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? t('reset.guncelleniyor') : t('reset.guncelle')}
        </Button>

        <Link
          to="/auth/login"
          className="block text-center text-sm text-primary hover:underline"
        >
          ← Giriş ekranına dön
        </Link>
      </form>
    </AuthLayout>
  );
}
